import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, X, FileText, User, Building2, ShoppingCart, Briefcase,
         Tag, Hammer, LayoutGrid, Navigation } from 'lucide-react';
import { apiGet } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const ICON_BY_TYPE = {
  project: Briefcase,
  employee: User,
  vendor: Building2,
  purchase_order: ShoppingCart,
  lead: Tag,
  enquiry: FileText,
  boq_item: Hammer,
  page: Navigation,
};

const TYPE_LABELS = {
  project: 'Project',
  employee: 'Employee',
  vendor: 'Vendor',
  purchase_order: 'Purchase Order',
  lead: 'Lead',
  enquiry: 'Enquiry',
  boq_item: 'BOQ Item',
  page: 'Pages & Settings',
};

// ── ERP Page Index: all navigable pages with their required module key ──────
// key matches the `allowed_modules` array on the user doc.
// key: null = always visible (dashboard-level)
const ERP_PAGES = [
  // Overview
  { label: 'Overview / Dashboard', path: '/app/overview', key: null },
  // Business Development
  { label: 'CRM Pipeline', path: '/app/crm/pipeline', key: 'crm' },
  { label: 'CRM Contacts', path: '/app/crm/contacts', key: 'crm' },
  { label: 'CRM Companies', path: '/app/crm/companies', key: 'crm' },
  { label: 'Lead Tracker', path: '/app/crm/leads', key: 'crm' },
  { label: 'Opportunity Finder AI', path: '/app/crm/ai-opportunities', key: 'crm' },
  { label: 'Potential Vendors', path: '/app/crm/potential-vendors', key: 'crm' },
  { label: 'Pre-Qualification Tracker', path: '/app/crm/prequalification', key: 'crm' },
  // Tendering
  { label: 'Bid Estimation', path: '/app/estimation/bid', key: 'tendering' },
  // Task Management
  { label: 'My Tasks', path: '/app/my-tasks', key: null },
  { label: 'Task Board', path: '/app/task-board', key: null },
  { label: 'Dependencies & Approvals', path: '/app/task-dependencies', key: null },
  // Projects
  { label: 'Project Management', path: '/app/projects', key: 'projects' },
  { label: 'WBS / Gantt', path: '/app/projects', key: 'projects' },
  { label: 'Daily Progress (DPR)', path: '/app/dpr', key: 'dpr' },
  { label: 'Hindrance Register', path: '/app/hindrances', key: 'dpr' },
  { label: 'Approvals Hub', path: '/app/approvals', key: null },
  // Safety / HSE
  { label: 'Safety Observations', path: '/app/safety/observations', key: 'hse' },
  { label: 'Near Miss', path: '/app/safety/near-miss', key: 'hse' },
  { label: 'Permit to Work', path: '/app/safety/permits', key: 'hse' },
  // Quality
  { label: 'Quality Dashboard', path: '/app/quality', key: 'quality' },
  { label: 'NCR / CAPA', path: '/app/quality/ncr', key: 'quality' },
  { label: 'Cube Tests', path: '/app/quality/cube-tests', key: 'quality' },
  // Labour Attendance
  { label: 'Labour Attendance', path: '/app/labour-attendance', key: 'labour' },
  // Procurement
  { label: 'Procurement MPR', path: '/app/procurement', key: 'procurement' },
  { label: 'Procurement RFQ', path: '/app/procurement', key: 'procurement' },
  { label: 'Purchase Orders', path: '/app/procurement', key: 'procurement' },
  { label: 'Vendor Master', path: '/app/procurement/vendors', key: 'procurement' },
  { label: 'Work Orders', path: '/app/procurement/work-orders', key: 'procurement' },
  { label: 'Material Master', path: '/app/procurement/material-master', key: 'inventory' },
  // Stores
  { label: 'Stores & Reconciliation', path: '/app/stores', key: 'inventory' },
  { label: 'Stores MPR', path: '/app/stores?tab=mpr', key: 'inventory' },
  { label: 'GRN', path: '/app/stores?tab=grn', key: 'inventory' },
  { label: 'Material Requisitions', path: '/app/stores?tab=mr', key: 'inventory' },
  { label: 'Stocktake', path: '/app/stores?tab=stocktake', key: 'inventory' },
  { label: 'Vendor Invoices', path: '/app/stores?tab=vendor-invoices', key: 'procurement' },
  // Petty Cash
  { label: 'Petty Cash', path: '/app/petty-cash', key: 'petty_cash' },
  { label: 'Petty Cash Finance Queue', path: '/app/petty-cash/finance-queue', key: 'finance' },
  // Fuel Management
  { label: 'Fuel Dashboard', path: '/app/fuel', key: 'fuel' },
  { label: 'Vehicle Master', path: '/app/fuel/vehicles', key: 'fuel' },
  { label: 'Fuel Logs', path: '/app/fuel/logs', key: 'fuel' },
  { label: 'Driver List', path: '/app/fuel/drivers', key: 'fuel' },
  // Finance
  { label: 'Finance Payments', path: '/app/finance/payments', key: 'finance' },
  { label: 'Client RA Bills', path: '/app/finance/client-bills', key: 'billing' },
  { label: 'EPC Ledgers', path: '/app/finance/epc-ledgers', key: 'finance' },
  { label: 'Bank Guarantees', path: '/app/finance/bg-register', key: 'finance' },
  { label: 'TDS Centre', path: '/app/finance/tds', key: 'finance' },
  { label: 'GST Centre', path: '/app/finance/gst', key: 'finance' },
  { label: 'CFO Pack & Close', path: '/app/finance/cfo', key: 'finance' },
  { label: 'Shadow GL', path: '/app/finance/gl', key: 'finance' },
  // People & HR
  { label: 'HR Dashboard', path: '/app/hr/dashboard', key: 'hr' },
  { label: 'Employee Master', path: '/app/hr/employees', key: 'hr' },
  { label: 'Payroll', path: '/app/hr/payroll', key: 'hr' },
  { label: 'Attendance Roster', path: '/app/hr/attendance', key: 'hr' },
  { label: 'Onboarding', path: '/app/hr/onboarding', key: 'hr' },
  { label: 'Training & Certifications', path: '/app/hr/training', key: 'hr' },
  { label: 'Compliance Tracker', path: '/app/hr/compliance', key: 'hr' },
  { label: 'Recruitment Dashboard', path: '/app/hr/recruitment', key: 'hr' },
  { label: 'Candidate Pool', path: '/app/hr/recruitment/pool', key: 'hr' },
  { label: 'Resume Scanner', path: '/app/hr/recruitment/scanner', key: 'hr' },
  { label: 'Job Openings', path: '/app/hr/recruitment/jobs', key: 'hr' },
  // Equipment
  { label: 'Equipment Fleet', path: '/app/equipment', key: 'equipment' },
  // Reports
  { label: 'MIS Reports', path: '/app/reports/mis', key: 'reports' },
  { label: 'Daily Attendance Report', path: '/app/reports/daily-attendance', key: 'reports' },
  { label: 'Procurement Report', path: '/app/reports/procurement', key: 'reports' },
  // Admin
  { label: 'User Management', path: '/app/user-management', key: 'admin_module' },
  { label: 'Settings', path: '/app/settings', key: 'settings' },
  { label: 'Email Subscriptions', path: '/app/settings/email-subscriptions', key: 'settings' },
  { label: 'Notification Subscriptions', path: '/app/settings/notification-subscriptions', key: 'settings' },
  { label: 'Activity Log', path: '/app/settings/audit-log', key: 'settings' },
  { label: 'Email Diagnostics', path: '/app/settings/email-diagnostics', key: 'settings' },
  { label: 'Email Settings (SMTP)', path: '/app/settings/email', key: 'settings' },
  { label: 'Office Locations', path: '/app/settings/office-locations', key: 'settings' },
  { label: 'Approval Matrix', path: '/app/settings/approval-workflows', key: 'settings' },
  // Administration module
  { label: 'Admin Assets', path: '/app/admin/assets', key: 'admin_module' },
  { label: 'Visitor Management', path: '/app/admin/visitors', key: 'admin_module' },
  { label: 'Office Supplies', path: '/app/admin/supplies', key: 'admin_module' },
];

/**
 * GlobalSearch — debounced, multi-collection ERP search.
 *
 * Searches backend entities (projects, employees, POs, tenders) AND
 * ERP page navigation items, RBAC-filtered so users only see what they
 * have access to.
 */
export default function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hits, setHits] = useState([]);
  const [anchorRect, setAnchorRect] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // ── RBAC-filtered page index ─────────────────────────────────────────────
  const allowedModules = user?.allowed_modules;
  const isAllModules = allowedModules === 'all' || !allowedModules;
  const allowedSet = Array.isArray(allowedModules) ? new Set(allowedModules) : null;

  const canSee = (key) => {
    if (!key) return true;  // always visible
    if (isAllModules) return true;
    return allowedSet?.has(key) ?? true;
  };

  const pageHits = (query) => {
    if (!query || query.trim().length < 2) return [];
    const qLower = query.toLowerCase();
    return ERP_PAGES
      .filter(p => canSee(p.key) && p.label.toLowerCase().includes(qLower))
      .slice(0, 6)
      .map(p => ({ type: 'page', title: p.label, subtitle: p.path.replace('/app/', ''), link: p.path }));
  };

  // Recompute dropdown anchor
  useEffect(() => {
    if (!open) return;
    const compute = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (r) setAnchorRect({ left: r.left, top: r.bottom + 4, width: Math.max(r.width, 360) });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, q]);

  const runSearch = useCallback(async (term) => {
    if ((term || '').trim().length < 2) {
      setHits([]); setLoading(false); return;
    }
    setLoading(true);
    try {
      const r = await apiGet('/search', { q: term });
      const entityHits = (r.hits || []).filter(h => {
        // RBAC filter on entity types
        const typeToModule = {
          project: 'projects', employee: 'hr', vendor: 'procurement',
          purchase_order: 'procurement', lead: 'crm', enquiry: 'tendering',
          boq_item: 'projects',
        };
        return canSee(typeToModule[h.type] ?? null);
      });
      // Merge pages first (navigation), then entities
      const pages = pageHits(term);
      setHits([...pages, ...entityHits]);
    } catch {
      // Even if backend fails, show page matches
      setHits(pageHits(term));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedModules]);

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 220);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  // Outside-click close
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Keyboard shortcut: Ctrl/Cmd+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape' && open) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const goto = (link) => {
    if (!link) return;
    setOpen(false);
    setQ('');
    navigate(link);
  };

  // Group hits — pages first, then entities
  const grouped = hits.reduce((acc, h) => {
    (acc[h.type] = acc[h.type] || []).push(h);
    return acc;
  }, {});

  // Put 'page' type first in display order
  const orderedTypes = ['page', ...Object.keys(grouped).filter(t => t !== 'page')];

  return (
    <div ref={wrapRef} className="relative flex-1 max-w-md min-w-0">
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input
        ref={inputRef}
        data-testid="global-search"
        type="text"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search projects, employees, pages, settings…  (Ctrl+K)"
        className="w-full pl-9 pr-9 py-2 bg-slate-50 border border-slate-200 rounded-sm text-sm focus:bg-white focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
      />
      {q && (
        <button
          type="button"
          onClick={() => { setQ(''); setHits([]); inputRef.current?.focus(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
          title="Clear"
          data-testid="global-search-clear"
        >
          <X size={13} />
        </button>
      )}

      {open && q.trim().length >= 2 && anchorRect && (
        <div
          className="fixed bg-white border border-slate-200 rounded-sm shadow-2xl max-h-[480px] overflow-y-auto"
          style={{
            left: `${anchorRect.left}px`,
            top: `${anchorRect.top}px`,
            width: `${Math.min(anchorRect.width + 60, 480)}px`,
            maxWidth: '90vw',
            zIndex: 9999,
          }}
          data-testid="global-search-results"
        >
          {loading && (
            <div className="flex items-center gap-2 px-4 py-3 text-xs text-slate-500">
              <Loader2 size={12} className="animate-spin" /> Searching…
            </div>
          )}
          {!loading && hits.length === 0 && (
            <div className="px-4 py-6 text-center text-xs text-slate-400">
              No results for <span className="font-display font-bold">"{q}"</span>
            </div>
          )}
          {!loading && hits.length > 0 && (
            <div>
              {orderedTypes.filter(t => grouped[t]).map((type) => {
                const items = grouped[type];
                if (!items?.length) return null;
                const Icon = ICON_BY_TYPE[type] || Search;
                return (
                  <div key={type}>
                    <div className="px-4 py-1.5 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 bg-slate-50 border-y border-slate-200">
                      {TYPE_LABELS[type] || type} · {items.length}
                    </div>
                    {items.map((h, i) => (
                      <button
                        type="button"
                        key={`${type}-${i}`}
                        onClick={() => goto(h.link)}
                        className="w-full text-left flex items-start gap-3 px-4 py-2.5 hover:bg-indigo-50/40 border-b border-slate-100"
                        data-testid={`global-search-hit-${type}-${i}`}
                      >
                        <div className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 ${
                          type === 'page' ? 'bg-emerald-50 text-emerald-700' : 'bg-indigo-50 text-[#3b4872]'
                        }`}>
                          <Icon size={14} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-display font-semibold text-slate-800 truncate">{h.title}</div>
                          {h.subtitle && <div className="text-[11px] text-slate-500 truncate">{h.subtitle}</div>}
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
          <div className="px-4 py-2 text-[10px] text-slate-400 bg-slate-50/60 border-t border-slate-200 flex items-center justify-between">
            <span>Press <kbd className="font-mono bg-white border border-slate-200 rounded-sm px-1">Esc</kbd> to close</span>
            <span>{hits.length} hit{hits.length === 1 ? '' : 's'}</span>
          </div>
        </div>
      )}
    </div>
  );
}
